import {Component, computed, EventEmitter, inject, Input, Output, signal} from '@angular/core';
import {MatTooltip} from '@angular/material/tooltip';
import {AssetVersionPipe} from '../../../../pipes/asset-version.pipe';
import {PolarisRuntimeFacadeService} from '../../../../services/polaris/polaris-runtime-facade.service';
import {PolarisChatHistoryItemVm} from '../../../../services/polaris/model';
import {PosthogService} from '../../../../services/posthog.service';
import {PosthogEventName} from '../../../../services/types';
import {validateChatTitle} from '../../../../utils/validators';

@Component({
  selector: 'app-chat-history-item',
  standalone: true,
  imports: [MatTooltip, AssetVersionPipe],
  templateUrl: './chat-history-item.component.html',
  styleUrl: './chat-history-item.component.scss',
})
export class ChatHistoryItemComponent {
  private polarisRuntimeFacadeService = inject(PolarisRuntimeFacadeService);
  private posthogService = inject(PosthogService);

  @Input({required: true}) chat!: PolarisChatHistoryItemVm;
  @Input() isActive = false;
  @Output() navigate = new EventEmitter<void>();

  protected isLegacyFlow = computed(() => this.polarisRuntimeFacadeService.isUsingLegacyFlow());

  public isEditing = signal(false);
  public editingTitle = signal('');
  public editingError = signal<string | null>(null);
  private editingOriginalTitle = '';

  public onItemClick(): void {
    if (!this.isEditing()) {
      this.navigate.emit();
    }
  }

  public startEditing(event: Event): void {
    event.stopPropagation();
    this.setEditState(true, this.chat.title);
  }

  public async confirmEdit(event?: Event): Promise<void> {
    event?.stopPropagation();
    const newTitle = this.editingTitle().trim();

    if (newTitle === this.editingOriginalTitle.trim()) {
      this.cancelEdit();
      return;
    }

    const error = validateChatTitle(newTitle);
    if (error) {
      this.editingError.set(error);
      return;
    }

    this.cancelEdit();
    this.posthogService.capture(PosthogEventName.CHAT_TITLE_EDITED, {chat_id: this.chat.id});

    try {
      await this.polarisRuntimeFacadeService.updateChatTitle(this.chat.id, newTitle);
    } catch {
      this.setEditState(true, newTitle);
      this.editingError.set('Failed to save. Please try again.');
    }
  }

  public cancelEdit(): void {
    this.setEditState(false, '');
  }

  public onTitleInput(event: Event): void {
    this.editingTitle.set((event.target as HTMLInputElement).value);
  }

  public onEditKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') void this.confirmEdit();
    if (event.key === 'Escape') this.cancelEdit();
  }

  private setEditState(active: boolean, title: string): void {
    this.isEditing.set(active);
    this.editingTitle.set(title);
    this.editingOriginalTitle = title;
    this.editingError.set(null);
  }
}
